import React from 'react';
import styled from 'styled-components';

import { colors } from '../../../core';
import Label from '../Label';
import Text from '../Text';

export interface TextAreaInputProps {
  id?: string;
  disabled?: boolean;
  label?: string;
  onChange: (changeEvent: any) => void;
  rows?: number;
  width?: string;
  placeholder?: string;
  defaultValue?: string;
  onKeyPress?: (e: any) => void;
  errorMessage?: string;
  variant?: 'primary' | 'secondary';
}

const Container = styled.div<{ width: string }>`
  width: ${({ width }) => width};
  position: relative;
`;

const StyledTextArea = styled.textarea<{ isError?: boolean; variant: 'primary' | 'secondary' }>`
  -webkit-appearance: none;
  border: 1px solid;
  border-color: ${({ isError, theme, variant }) =>
    isError
      ? theme.errorColor
      : (variant === 'primary'
          ? theme.textAreaInputBorderColor
          : theme.textAreaInputSecondaryBorderColor) || colors.smoke};
  border-radius: ${({ theme }) => theme.borderRadius || '3px'};
  color: ${({ theme }) => theme.textAreaInputTextColor || colors.ash};
  display: block;
  font-family: ${({ theme }) => theme.fontFamily || `Quicksand, sans-serif`};
  font-size: ${({ theme }) => theme.fontSize || '14px'};
  font-weight: ${({ theme }) => theme.fontWeight || 600};
  padding: 8px 10px;
  resize: vertical;
  width: 100%;

  ::placeholder {
    color: ${({ theme }) => theme.textAreaInputPlaceholderColor || colors.smoke};
  }

  &:hover {
    box-shadow: 0 2px 4px 0 rgba(217, 217, 217, 0.5);
  }

  &:active,
  &:focus {
    color: ${({ theme }) => theme.textAreaInputFocusTextColor || colors.charcoal};
    border-color: ${({ theme }) => theme.textAreaInputFocusBorderColor || colors.primary};
    outline: 0;
  }

  &:disabled {
    background-color: ${({ theme }) =>
      !theme.textInputShowOnlyBottomBorder
        ? theme.textInputDisabledBackgroundColor || colors.pearl
        : 'transparent'};
    border-color: ${({ theme }) => theme.textAreaInputDisabledBorderColor || colors.cloud};
    box-shadow: none;
    color: ${({ theme }) => theme.textAreaInputDisabledTextColor || colors.smoke};
    cursor: default;
  }
`;

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
  padding: ${({ theme }) => theme.inputErrorMessagePadding || '0'};
  line-height: 1.5;
`;

const TextAreaInput = ({
  label,
  onChange,
  rows,
  width,
  errorMessage,
  variant = 'primary',
  ...inputProps
}: TextAreaInputProps) => (
  <Container width={width || '100%'}>
    {label && <Label htmlFor={inputProps.id}>{label}</Label>}
    <StyledTextArea
      {...inputProps}
      rows={rows}
      onChange={(changeEvent) => onChange(changeEvent.target.value)}
      isError={!!errorMessage}
      variant={variant}
    />
    {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
  </Container>
);

TextAreaInput.defaultProps = {
  rows: 3,
};

export default TextAreaInput;
